import * as React from "react"

import { BuildingStep } from "./BuildingStep"
import { type BuildTask } from "./types"

/**
 * `BuildingStep` with its checklist advancing on a timer.
 *
 * The staging lives here rather than in the step so the step stays what its own
 * note says it is — presentation with no opinion about progress — and so the
 * flow that mounts this doesn't grow a timer chain and the cleanup that comes
 * with one.
 *
 * The interval is a deliberate fiction. Nothing the flow does at this point
 * reports per-task progress: the source has already been validated by the time
 * this screen appears, and the one real request in flight is the post preview,
 * which the caller fires alongside this and does not wait for. The rows name
 * work that genuinely happened (or is happening), but their timing is a paced
 * reveal, not telemetry — see BuildingStep's note before wiring anything to it.
 */
export function BuildingProgress({
  heading,
  subtitle,
  labels,
  stepMs = 750,
  settleMs = 400,
  onComplete,
}: {
  heading: string
  subtitle?: string
  labels: string[]
  /**
   * Dwell per row. At 500 a row finished before its label could be read, so the
   * screen registered as one flash rather than four steps; 750 is long enough to
   * read three or four words and still puts the whole checklist under 3s.
   */
  stepMs?: number
  /**
   * Hold after the last row lands, before `onComplete`. Without it the completed
   * checklist renders and is navigated away from in the same beat, so the state
   * the whole screen builds towards is never actually seen.
   */
  settleMs?: number
  /** Fired once, after the last row lands and the hold elapses. */
  onComplete?: () => void
}) {
  const [completed, setCompleted] = React.useState(0)

  const total = labels.length

  React.useEffect(() => {
    if (completed >= total) return

    const timer = window.setTimeout(() => {
      setCompleted((done) => done + 1)
    }, stepMs)

    return () => window.clearTimeout(timer)
  }, [completed, total, stepMs])

  // A ref, not just the `completed >= total` test: React runs effects twice in
  // development, and the caller's onComplete navigates.
  const fired = React.useRef(false)

  // The callback is read through a ref so it can stay out of the effect's deps.
  // An inline `onComplete={() => …}` is a new function every render, and with it
  // in the deps any re-render inside the settle window would clear the pending
  // timer and start a new one — a parent re-rendering faster than settleMs would
  // never advance at all.
  const latest = React.useRef(onComplete)
  React.useEffect(() => {
    latest.current = onComplete
  })

  React.useEffect(() => {
    if (completed < total || fired.current) return

    // The guard is inside the callback, not beside the schedule: in development
    // the effect runs twice, and marking it fired up front would let the first
    // run's cleanup cancel the only timer while the second run returned early —
    // onComplete would never fire at all.
    const timer = window.setTimeout(() => {
      if (fired.current) return
      fired.current = true
      latest.current?.()
    }, settleMs)

    return () => window.clearTimeout(timer)
  }, [completed, total, settleMs])

  const tasks: BuildTask[] = labels.map((label, i) => ({
    label,
    status: i < completed ? "done" : i === completed ? "running" : "todo",
  }))

  return <BuildingStep heading={heading} tasks={tasks} subtitle={subtitle} />
}
